import { computed, type Ref } from 'vue';
import type { SelectionItem, SelectionItemOfficeOffer } from '@models/selectionItem.ts';
import type { Url } from '@global-types/url.ts';
import type { SerializedOffer } from '@models/offer.ts';
import { isOfferDisabled } from '@organisms/offer/offer-utils';

export const useSelectionItemOfferUrl = (selectionItem: Ref<SelectionItem>) => {
  const firstOffer = computed<SerializedOffer>(() => selectionItem.value.offer);

  const availableOffers = computed<SelectionItemOfficeOffer[]>(
    () => selectionItem.value.offers.filter((offer) => !isOfferDisabled(offer)),
  );

  const offerUrl = computed<Url | undefined>(() => {
    if (selectionItem.value.is_all_offers_rented) {
      return undefined;
    }

    let baseUrl: string = `${firstOffer.value.public_url}`;
    const parameters: string[] = [];

    if (selectionItem.value.proposal?.id) {
      parameters.push(`proposal_id=${selectionItem.value.proposal.id}`);
    }

    if (selectionItem.value.team_size) {
      parameters.push(`team-size=${selectionItem.value.team_size}`);
    }

    if (selectionItem.value.offers.length > 1) {
      const hashIds = availableOffers.value.map((offer) => offer.hash_id);

      const teamSizes: number[] = availableOffers.value
        .filter((offer) => offer.team_size !== null)
        .map((offer) => offer.team_size as number);

      parameters.push(`selected_offers=${encodeURIComponent(hashIds.join('|'))}`);

      if (teamSizes.length) {
        parameters.push(`team-size-list=${encodeURIComponent(teamSizes.join('|'))}`);
      }
    }

    if (parameters.length) {
      baseUrl += `?${parameters.join('&')}`;
    }

    return baseUrl;
  });

  const openOffer = (event: Event): void => {
    if (offerUrl.value || !selectionItem.value.offers.length) {
      return;
    }

    event.preventDefault();
  };

  return {
    offerUrl,
    openOffer,
  };
};
